import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ── Constants ────────────────────────────────────────────────

export const DAYS_ORDER = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday',
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKENDS = ['Saturday', 'Sunday'];

const TODAY_INDEX = new Date().getDay(); // 0=Sun, 1=Mon...
const TODAY_DAY = DAYS_ORDER[TODAY_INDEX === 0 ? 6 : TODAY_INDEX - 1];

export interface DayHours {
  day: string;
  hours: string;
}

export const EMPTY_TRADING_HOURS: DayHours[] = DAYS_ORDER.map((day) => ({
  day,
  hours: '',
}));

// ── Trading Hours Parser ────────────────────────────────────

/**
 * Parse trading hours from the /getparentattributes/trading-hours endpoint.
 * Each item has shape: { value: { type: "Monday", start: "09:00", end: "17:00" } }
 */
export const parseTradingHours = (
  data: Array<Record<string, unknown>> | undefined,
): DayHours[] | null => {
  if (!data?.length) return null;

  const dayMap: Record<string, string> = {};

  for (const attr of data) {
    const val = attr.value as Record<string, unknown> | undefined;
    if (!val || typeof val !== 'object') continue;

    // Primary shape: { type: "Monday", start: "09:00", end: "17:00" }
    const dayName = (val.type ?? val.day) as string | undefined;
    if (dayName) {
      const start = (val.start ?? val.openTime ?? val.open ?? '') as string;
      const end = (val.end ?? val.closeTime ?? val.close ?? '') as string;
      const hours = start || end ? `${start} - ${end}`.trim() : '';
      // Capitalise to match DAYS_ORDER
      const key = dayName.charAt(0).toUpperCase() + dayName.slice(1).toLowerCase();
      if (hours) dayMap[key] = hours;
    }
  }

  if (Object.keys(dayMap).length === 0) return null;

  return DAYS_ORDER.map((day) => ({
    day,
    hours: dayMap[day] || '',
  }));
};

// ── SLA Coverage Parser ─────────────────────────────────────

const expandCoverageType = (type: string): string[] => {
  switch (type) {
    case 'All Days': return DAYS_ORDER;
    case 'Weekdays': return WEEKDAYS;
    case 'Weekends': return WEEKENDS;
    default: return DAYS_ORDER.includes(type) ? [type] : [];
  }
};

/**
 * Parse SLA coverage hours from the /getparentattributes/coverages endpoint.
 * Each item has shape: { value: { checked: true, tradingHours: { type, start, end } } }
 */
export const parseCoverageHours = (
  data: Array<Record<string, unknown>> | undefined,
): DayHours[] | null => {
  if (!data?.length) return null;

  const enabled = data.filter((attr) => {
    const val = attr.value as Record<string, unknown> | undefined;
    return val?.checked;
  });

  if (enabled.length === 0) return null;

  const dayMap: Record<string, string> = {};

  for (const attr of enabled) {
    const val = attr.value as Record<string, unknown> | undefined;
    const th = val?.tradingHours as Record<string, string> | undefined;
    if (!th?.type) continue;

    const hours = th.start || th.end ? `${th.start || ''} - ${th.end || ''}`.trim() : '';
    const days = expandCoverageType(th.type);

    for (const day of days) {
      if (!dayMap[day] && hours) dayMap[day] = hours;
    }
  }

  if (Object.keys(dayMap).length === 0) return null;

  return DAYS_ORDER.map((day) => ({
    day,
    hours: dayMap[day] || '',
  }));
};

// ── Display component ────────────────────────────────────────

/** Hours display for a single schedule -- all 7 days, current day highlighted. */
export const HoursDisplay: React.FC<{ days: DayHours[]; emptyText?: string }> = ({
  days,
  emptyText = 'Closed',
}) => (
  <Box>
    {days.map(({ day, hours }) => {
      const isToday = day === TODAY_DAY;
      return (
        <Box
          key={day}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 0.25,
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: isToday ? 'v4.tableSelected' : 'transparent',
            fontWeight: isToday ? 600 : 400,
          }}
        >
          <Typography sx={{ fontSize: '11px', fontWeight: 'inherit', minWidth: 80 }}>
            {day}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 'inherit',
              color: hours ? 'text.primary' : 'text.secondary',
            }}
          >
            {hours || emptyText}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/** @deprecated Use HoursDisplay or CombinedHoursDisplay instead */
export const TradingHoursDisplay = HoursDisplay;

/** Combined display — one row per day with Trading Hours and SLA Coverage columns. */
export const CombinedHoursDisplay: React.FC<{
  tradingHours: DayHours[];
  coverageHours: DayHours[];
}> = ({ tradingHours, coverageHours }) => {
  const coverageMap: Record<string, string> = {};
  for (const { day, hours } of coverageHours) {
    coverageMap[day] = hours;
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '80px 1fr 1fr',
          gap: 1,
          px: 0.5,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'v4.border',
          mb: 0.25,
        }}
      >
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary' }}>Day</Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>Trading Hours</Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>SLA Coverage</Typography>
      </Box>

      {tradingHours.map(({ day, hours: trading }) => {
        const coverage = coverageMap[day] || '';
        const isToday = day === TODAY_DAY;
        return (
          <Box
            key={day}
            sx={{
              display: 'grid',
              gridTemplateColumns: '80px 1fr 1fr',
              gap: 1,
              py: 0.25,
              px: 0.5,
              borderRadius: 0.5,
              bgcolor: isToday ? 'v4.tableSelected' : 'transparent',
              fontWeight: isToday ? 600 : 400,
            }}
          >
            <Typography sx={{ fontSize: '11px', fontWeight: 'inherit' }}>{day}</Typography>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 'inherit',
                textAlign: 'right',
                color: trading ? 'text.primary' : 'text.secondary',
              }}
            >
              {trading || 'Closed'}
            </Typography>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 'inherit',
                textAlign: 'right',
                color: coverage ? 'text.primary' : 'text.secondary',
              }}
            >
              {coverage || 'No Coverage'}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
